import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BuildingOfficeIcon,
  UsersIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const DepartmentDirectory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    // Sample department data
    const departmentData = [
      {
        id: 1,
        name: "Cardiology",
        description: "Comprehensive cardiac care and cardiovascular treatments",
        headOfDepartment: "Dr. Anita Reddy",
        employeeCount: 15,
        location: "Block A, Floors 3-4",
        phone: "+91 98765 43210",
        email: "cardiology@apollo.com",
        services: [
          "Cardiac Catheterization",
          "Angioplasty",
          "Heart Surgery",
          "Echocardiography",
          "Cardiac Rehabilitation"
        ],
        image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=300&h=200&fit=crop",
        employees: [
          { id: 1, name: "Dr. Anita Reddy", position: "Cardiologist" },
          { id: 7, name: "Dr. Suresh Kumar", position: "Cardiac Surgeon" },
          { id: 8, name: "Nurse Priya", position: "Cardiac Care Nurse" }
        ]
      },
      {
        id: 2,
        name: "Emergency Medicine",
        description: "24/7 emergency care and critical medical services",
        headOfDepartment: "Dr. Rajesh Sharma",
        employeeCount: 24,
        location: "Emergency Wing",
        phone: "+91 98765 43211",
        email: "emergency@apollo.com",
        services: [
          "Emergency Care",
          "Trauma Surgery",
          "Critical Care",
          "Ambulance Services",
          "Emergency Procedures"
        ],
        image: "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=300&h=200&fit=crop",
        employees: [
          { id: 2, name: "Arjun Singh", position: "Nurse Practitioner" },
          { id: 9, name: "Dr. Rajesh Sharma", position: "Emergency Physician" },
          { id: 10, name: "Nurse Maya", position: "Emergency Nurse" }
        ]
      },
      {
        id: 3,
        name: "Laboratory Services",
        description: "Advanced diagnostic testing and pathology services",
        headOfDepartment: "Dr. Vikram Patel",
        employeeCount: 12,
        location: "Laboratory Block",
        phone: "+91 98765 43212",
        email: "lab@apollo.com",
        services: [
          "Blood Tests",
          "Pathology",
          "Microbiology",
          "Biochemistry",
          "Imaging Studies"
        ],
        image: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=300&h=200&fit=crop",
        employees: [
          { id: 3, name: "Deepika Joshi", position: "Medical Technologist" },
          { id: 11, name: "Dr. Vikram Patel", position: "Pathologist" },
          { id: 12, name: "Ravi Kumar", position: "Lab Technician" }
        ]
      },
      {
        id: 4,
        name: "Orthopedics",
        description: "Bone, joint, and musculoskeletal system treatments",
        headOfDepartment: "Dr. Rajesh Kumar",
        employeeCount: 10,
        location: "Block B, Floor 2",
        phone: "+91 98765 43213",
        email: "orthopedics@apollo.com",
        services: [
          "Joint Replacement",
          "Fracture Treatment",
          "Sports Medicine",
          "Arthroscopy",
          "Spine Surgery"
        ],
        image: "https://images.unsplash.com/photo-1559757175-0eb30cd0c5c9?w=300&h=200&fit=crop",
        employees: [
          { id: 4, name: "Dr. Rajesh Kumar", position: "Orthopedic Surgeon" },
          { id: 13, name: "Dr. Kavya Reddy", position: "Orthopedic Specialist" },
          { id: 14, name: "Therapy Assistant Ram", position: "Physical Therapist" }
        ]
      },
      {
        id: 5,
        name: "Rehabilitation",
        description: "Physical therapy and rehabilitation services",
        headOfDepartment: "Priya Sharma",
        employeeCount: 8,
        location: "Rehabilitation Center",
        phone: "+91 98765 43214",
        email: "rehab@apollo.com",
        services: [
          "Physical Therapy",
          "Occupational Therapy",
          "Speech Therapy",
          "Sports Rehabilitation",
          "Pain Management"
        ],
        image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop",
        employees: [
          { id: 5, name: "Priya Sharma", position: "Physiotherapist" },
          { id: 15, name: "Dr. Anil Gupta", position: "Rehabilitation Specialist" },
          { id: 16, name: "Therapy Assistant Sita", position: "Occupational Therapist" }
        ]
      },
      {
        id: 6,
        name: "Pediatrics",
        description: "Specialized medical care for infants, children, and adolescents",
        headOfDepartment: "Dr. Meera Patel",
        employeeCount: 18,
        location: "Children's Wing",
        phone: "+91 98765 43215",
        email: "pediatrics@apollo.com",
        services: [
          "Pediatric Care",
          "Neonatology",
          "Pediatric Surgery",
          "Child Psychology",
          "Vaccination Services"
        ],
        image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=300&h=200&fit=crop",
        employees: [
          { id: 6, name: "Dr. Meera Patel", position: "Pediatrician" },
          { id: 17, name: "Dr. Sanjay Verma", position: "Pediatric Surgeon" },
          { id: 18, name: "Nurse Sunita", position: "Pediatric Nurse" }
        ]
      }
    ];

    // Simulate loading
    setTimeout(() => {
      setDepartments(departmentData);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.headOfDepartment.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDepartmentClick = (departmentId) => {
    navigate(`/directory/department/${departmentId}`);
  };

  const handleEmployeeClick = (employeeId) => {
    navigate(`/directory/employee/${employeeId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading departments...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <BuildingOfficeIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-80" />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Department Directory</h1>
            <p className="text-lg sm:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Explore our specialized departments and medical services
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="relative max-w-lg mx-auto">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Departments Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {filteredDepartments.map(department => (
            <div
              key={department.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-apollo-accent/30"
            >
              {/* Department Header */}
              <div className="relative">
                <img
                  src={department.image}
                  alt={department.name}
                  className="w-full h-40 sm:h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                    {department.name}
                  </h2>
                  <div className="flex items-center text-white/90">
                    <UsersIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="text-xs sm:text-sm">{department.employeeCount} employees</span>
                  </div>
                </div>
              </div>

              {/* Department Info */}
              <div className="p-4 sm:p-6">
                <p className="text-gray-600 mb-4 leading-relaxed text-sm sm:text-base">
                  {department.description}
                </p>

                {/* Head of Department */}
                <div className="mb-4">
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Head of Department</p>
                  <p className="font-semibold text-apollo-primary text-sm sm:text-base">{department.headOfDepartment}</p>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 mb-1">Location</p>
                    <p className="text-xs sm:text-sm text-gray-700">{department.location}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 mb-1">Phone</p>
                    <p className="text-xs sm:text-sm text-gray-700 break-all">{department.phone}</p>
                  </div>
                </div>

                {/* Key Services */}
                <div className="mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm text-gray-500 mb-2">Key Services</p>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {department.services.slice(0, 3).map((service, index) => (
                      <span
                        key={index}
                        className="bg-apollo-accent/10 text-apollo-accent px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {service}
                      </span>
                    ))}
                    {department.services.length > 3 && (
                      <span className="text-xs text-gray-500 px-2 py-1">
                        +{department.services.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Sample Employees */}
                <div className="mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm text-gray-500 mb-3">Team Members</p>
                  <div className="space-y-2">
                    {department.employees.slice(0, 3).map(employee => (
                      <div
                        key={employee.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => handleEmployeeClick(employee.id)}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{employee.name}</p>
                          <p className="text-xs text-gray-600 truncate">{employee.position}</p>
                        </div>
                        <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0 ml-2" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => handleDepartmentClick(department.id)}
                    className="flex-1 bg-apollo-accent text-white py-2 sm:py-3 px-3 sm:px-4 rounded-xl hover:bg-apollo-accent/90 transition-colors font-medium text-xs sm:text-sm flex items-center justify-center space-x-2"
                  >
                    <UserGroupIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>View All Staff</span>
                  </button>
                  <button className="sm:flex-shrink-0 border border-apollo-accent text-apollo-accent py-2 sm:py-3 px-3 sm:px-4 rounded-xl hover:bg-apollo-accent/5 transition-colors font-medium text-xs sm:text-sm">
                    Contact
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredDepartments.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <BuildingOfficeIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No departments found</h3>
            <p className="text-gray-600 text-sm sm:text-base">Try adjusting your search terms</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DepartmentDirectory;
